import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookOpen, Settings, Filter, Download, Upload, X, Save, FileText, Focus, FileText as FileTextIcon } from 'lucide-react';

interface DyslexiaSettings {
  fontFamily: 'opendyslexic' | 'lexend' | 'comic-sans' | 'arial' | 'helvetica' | 'times' | 'georgia';
  fontSize: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
  lineHeight: 'tight' | 'normal' | 'relaxed' | 'loose' | 'very-loose';
  letterSpacing: 'tight' | 'normal' | 'wide' | 'wider' | 'widest';
  wordSpacing: 'tight' | 'normal' | 'wide' | 'wider' | 'widest';
  colorScheme: 'default' | 'high-contrast' | 'sepia' | 'blue-light' | 'yellow-tint';
  backgroundPattern: 'none' | 'subtle' | 'medium' | 'strong';
  textHighlighting: boolean;
  syllableBreaks: boolean;
  readingGuide: boolean;
  focusMode: boolean;
  audioSupport: boolean;
  visualCues: boolean;
  readingSpeed: 'slow' | 'normal' | 'fast';
  comprehensionMode: 'basic' | 'enhanced' | 'advanced';
}

interface DyslexiaSupportProps {
  onSettingsChange?: (settings: DyslexiaSettings) => void;
  onFontLoad?: (fontFamily: string) => void;
  onReadingTest?: (testType: string) => void;
}

const DyslexiaSupport: React.FC<DyslexiaSupportProps> = ({
  onSettingsChange,
  onFontLoad,
  onReadingTest
}) => {
  const [settings, setSettings] = useState<DyslexiaSettings>({
    fontFamily: 'opendyslexic',
    fontSize: 'medium',
    lineHeight: 'relaxed',
    letterSpacing: 'wide',
    wordSpacing: 'wide',
    colorScheme: 'default',
    backgroundPattern: 'none',
    textHighlighting: true,
    syllableBreaks: true,
    readingGuide: true,
    focusMode: false,
    audioSupport: true,
    visualCues: true,
    readingSpeed: 'normal',
    comprehensionMode: 'enhanced'
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showReadingTest, setShowReadingTest] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyDyslexiaSettings();
    loadDyslexiaFonts();
  }, [settings]);

  const applyDyslexiaSettings = () => {
    const root = document.documentElement;
    
    // Apply font family
    root.style.setProperty('--dyslexia-font-family', settings.fontFamily);
    
    // Apply font size
    const fontSizeMap = {
      'small': '14px',
      'medium': '16px',
      'large': '18px',
      'xlarge': '20px',
      'xxlarge': '24px'
    };
    root.style.setProperty('--dyslexia-font-size', fontSizeMap[settings.fontSize]);
    
    // Apply line height
    const lineHeightMap = {
      'tight': '1.2',
      'normal': '1.5',
      'relaxed': '1.8',
      'loose': '2.0',
      'very-loose': '2.5'
    };
    root.style.setProperty('--dyslexia-line-height', lineHeightMap[settings.lineHeight]);
    
    // Apply letter spacing
    const letterSpacingMap = {
      'tight': '0px',
      'normal': '0.5px',
      'wide': '1px',
      'wider': '1.5px',
      'widest': '2px'
    };
    root.style.setProperty('--dyslexia-letter-spacing', letterSpacingMap[settings.letterSpacing]);
    
    // Apply word spacing
    const wordSpacingMap = {
      'tight': '0px',
      'normal': '2px',
      'wide': '4px',
      'wider': '6px',
      'widest': '8px'
    };
    root.style.setProperty('--dyslexia-word-spacing', wordSpacingMap[settings.wordSpacing]);
    
    // Apply color scheme
    root.setAttribute('data-dyslexia-color-scheme', settings.colorScheme);
    
    // Apply background pattern
    root.setAttribute('data-dyslexia-background', settings.backgroundPattern);
    
    // Apply dyslexia-specific classes
    if (settings.textHighlighting) {
      root.classList.add('dyslexia-highlighting');
    } else {
      root.classList.remove('dyslexia-highlighting');
    }
    
    if (settings.syllableBreaks) {
      root.classList.add('dyslexia-syllable-breaks');
    } else {
      root.classList.remove('dyslexia-syllable-breaks');
    }
    
    if (settings.readingGuide) {
      root.classList.add('dyslexia-reading-guide');
    } else {
      root.classList.remove('dyslexia-reading-guide');
    }
    
    if (settings.focusMode) {
      root.classList.add('dyslexia-focus-mode');
    } else {
      root.classList.remove('dyslexia-focus-mode');
    }
    
    if (settings.visualCues) {
      root.classList.add('dyslexia-visual-cues');
    } else {
      root.classList.remove('dyslexia-visual-cues');
    }
    
    onSettingsChange?.(settings);
  };

  const loadDyslexiaFonts = async () => {
    try {
      setIsLoading(true);
      
      // Load OpenDyslexic font
      if (settings.fontFamily === 'opendyslexic') {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=OpenDyslexic:wght@400;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        onFontLoad?.('opendyslexic');
      }
      
      // Load Lexend font
      if (settings.fontFamily === 'lexend') {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        onFontLoad?.('lexend');
      }
      
    } catch (err) {
      setError('Failed to load dyslexia fonts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (key: keyof DyslexiaSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const runReadingTest = async (testType: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Simulate reading test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockResults = {
        testType,
        score: Math.floor(Math.random() * 40) + 60, // 60-100
        speed: Math.floor(Math.random() * 100) + 150, // 150-250 WPM
        comprehension: Math.floor(Math.random() * 30) + 70, // 70-100%
        recommendations: [
          'Consider increasing letter spacing',
          'Try a different font family',
          'Enable text highlighting',
          'Use reading guide for better focus'
        ],
        timestamp: new Date()
      };
      
      setTestResults(mockResults);
      onReadingTest?.(testType);
      
    } catch (err) {
      setError('Failed to run reading test');
    } finally {
      setIsLoading(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      fontFamily: 'opendyslexic',
      fontSize: 'medium',
      lineHeight: 'relaxed',
      letterSpacing: 'wide',
      wordSpacing: 'wide',
      colorScheme: 'default',
      backgroundPattern: 'none',
      textHighlighting: true,
      syllableBreaks: true,
      readingGuide: true,
      focusMode: false,
      audioSupport: true,
      visualCues: true,
      readingSpeed: 'normal',
      comprehensionMode: 'enhanced'
    });
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dyslexia-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const importSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target?.result as string);
          setSettings(importedSettings);
        } catch (error) {
          setError('Failed to import settings');
        }
      };
      reader.readAsText(file);
    }
  };

  const getFontDisplayName = (font: string) => {
    const fontMap: { [key: string]: string } = {
      'opendyslexic': 'OpenDyslexic',
      'lexend': 'Lexend',
      'comic-sans': 'Comic Sans MS',
      'arial': 'Arial',
      'helvetica': 'Helvetica',
      'times': 'Times New Roman',
      'georgia': 'Georgia'
    };
    return fontMap[font] || font;
  };

  const getColorSchemeDisplayName = (scheme: string) => {
    const schemeMap: { [key: string]: string } = {
      'default': 'Default',
      'high-contrast': 'High Contrast',
      'sepia': 'Sepia',
      'blue-light': 'Blue Light Filter',
      'yellow-tint': 'Yellow Tint'
    };
    return schemeMap[scheme] || scheme;
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Dyslexia Support
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Specialized fonts and reading aids to improve reading experience for users with dyslexia
        </p>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => runReadingTest('comprehension')}
            className="p-4 rounded-lg border-2 border-blue-200 dark:border-blue-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
          >
            <BookOpen className="h-6 w-6 mx-auto mb-2 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Reading Test</span>
          </button>

          <button
            onClick={() => setShowSettings(true)}
            className="p-4 rounded-lg border-2 border-green-200 dark:border-green-700 hover:border-green-500 dark:hover:border-green-400 transition-colors"
          >
            <Settings className="h-6 w-6 mx-auto mb-2 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Settings</span>
          </button>

          <button
            onClick={exportSettings}
            className="p-4 rounded-lg border-2 border-purple-200 dark:border-purple-700 hover:border-purple-500 dark:hover:border-purple-400 transition-colors"
          >
            <Download className="h-6 w-6 mx-auto mb-2 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Export</span>
          </button>

          <label className="p-4 rounded-lg border-2 border-orange-200 dark:border-orange-700 hover:border-orange-500 dark:hover:border-orange-400 transition-colors cursor-pointer">
            <Upload className="h-6 w-6 mx-auto mb-2 text-orange-600 dark:text-orange-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Import</span>
            <input
              type="file"
              accept=".json"
              onChange={importSettings}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Current Settings Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Current Settings Preview
        </h3>
        
        <div 
          ref={contentRef}
          className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
          style={{
            fontFamily: `var(--dyslexia-font-family, ${settings.fontFamily})`,
            fontSize: settings.fontSize === 'small' ? '14px' : 
                     settings.fontSize === 'medium' ? '16px' :
                     settings.fontSize === 'large' ? '18px' :
                     settings.fontSize === 'xlarge' ? '20px' : '24px',
            lineHeight: settings.lineHeight === 'tight' ? '1.2' :
                       settings.lineHeight === 'normal' ? '1.5' :
                       settings.lineHeight === 'relaxed' ? '1.8' :
                       settings.lineHeight === 'loose' ? '2.0' : '2.5',
            letterSpacing: settings.letterSpacing === 'tight' ? '0px' :
                          settings.letterSpacing === 'normal' ? '0.5px' :
                          settings.letterSpacing === 'wide' ? '1px' :
                          settings.letterSpacing === 'wider' ? '1.5px' : '2px',
            wordSpacing: settings.wordSpacing === 'tight' ? '0px' :
                        settings.wordSpacing === 'normal' ? '2px' :
                        settings.wordSpacing === 'wide' ? '4px' :
                        settings.wordSpacing === 'wider' ? '6px' : '8px'
          }}
        >
          <h4 className="text-lg font-semibold mb-2">Sample Text for Testing</h4>
          <p className="mb-2">
            This is a sample paragraph to demonstrate how the current dyslexia settings 
            affect the reading experience. The text should be easier to read with the 
            selected font, spacing, and color scheme.
          </p>
          <p>
            Notice how the letter spacing, word spacing, and line height work together 
            to create a more comfortable reading experience for users with dyslexia.
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Reading Test Results
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {testResults.score}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Overall Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {testResults.speed} WPM
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Reading Speed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {testResults.comprehension}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Comprehension</div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Recommendations:</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
              {testResults.recommendations.map((rec: string, index: number) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Dyslexia Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Font Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Font Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Font Family
                    </label>
                    <select
                      value={settings.fontFamily}
                      onChange={(e) => handleSettingChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="opendyslexic">OpenDyslexic</option>
                      <option value="lexend">Lexend</option>
                      <option value="comic-sans">Comic Sans MS</option>
                      <option value="arial">Arial</option>
                      <option value="helvetica">Helvetica</option>
                      <option value="times">Times New Roman</option>
                      <option value="georgia">Georgia</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Font Size
                    </label>
                    <select
                      value={settings.fontSize}
                      onChange={(e) => handleSettingChange('fontSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="xlarge">Extra Large</option>
                      <option value="xxlarge">XX Large</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Line Height
                    </label>
                    <select
                      value={settings.lineHeight}
                      onChange={(e) => handleSettingChange('lineHeight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="tight">Tight</option>
                      <option value="normal">Normal</option>
                      <option value="relaxed">Relaxed</option>
                      <option value="loose">Loose</option>
                      <option value="very-loose">Very Loose</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Letter Spacing
                    </label>
                    <select
                      value={settings.letterSpacing}
                      onChange={(e) => handleSettingChange('letterSpacing', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="tight">Tight</option>
                      <option value="normal">Normal</option>
                      <option value="wide">Wide</option>
                      <option value="wider">Wider</option>
                      <option value="widest">Widest</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Word Spacing
                    </label>
                    <select
                      value={settings.wordSpacing}
                      onChange={(e) => handleSettingChange('wordSpacing', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="tight">Tight</option>
                      <option value="normal">Normal</option>
                      <option value="wide">Wide</option>
                      <option value="wider">Wider</option>
                      <option value="widest">Widest</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Color Scheme
                    </label>
                    <select
                      value={settings.colorScheme}
                      onChange={(e) => handleSettingChange('colorScheme', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="default">Default</option>
                      <option value="high-contrast">High Contrast</option>
                      <option value="sepia">Sepia</option>
                      <option value="blue-light">Blue Light Filter</option>
                      <option value="yellow-tint">Yellow Tint</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Reading Aids */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Reading Aids
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Text Highlighting
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.textHighlighting}
                      onChange={(e) => handleSettingChange('textHighlighting', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Syllable Breaks
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.syllableBreaks}
                      onChange={(e) => handleSettingChange('syllableBreaks', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Reading Guide
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.readingGuide}
                      onChange={(e) => handleSettingChange('readingGuide', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Focus Mode
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.focusMode}
                      onChange={(e) => handleSettingChange('focusMode', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Audio Support
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.audioSupport}
                      onChange={(e) => handleSettingChange('audioSupport', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Visual Cues
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.visualCues}
                      onChange={(e) => handleSettingChange('visualCues', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Advanced Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Advanced Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reading Speed
                    </label>
                    <select
                      value={settings.readingSpeed}
                      onChange={(e) => handleSettingChange('readingSpeed', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="slow">Slow</option>
                      <option value="normal">Normal</option>
                      <option value="fast">Fast</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Comprehension Mode
                    </label>
                    <select
                      value={settings.comprehensionMode}
                      onChange={(e) => handleSettingChange('comprehensionMode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="basic">Basic</option>
                      <option value="enhanced">Enhanced</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={resetToDefaults}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Reset to Defaults
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default DyslexiaSupport;
